import * as THREE from 'three';

// درختِ جست‌وجوی مثلث‌ها (BVH) برای ردیابیِ پرتوِ نور روی کارت گرافیک.
// همه‌ی مثلث‌های صحنه (در مختصاتِ جهانی) در یک درخت؛ درخت و مثلث‌ها در دو «تصویرِ داده» (DataTexture) روی
// کارت گرافیک می‌روند و شیدر با texelFetch پیمایششان می‌کند.
//
// چیدمانِ گره (۲ خانه‌ی RGBA):   [minX minY minZ  a] [maxX maxY maxZ  b]
//   گره‌ی داخلی: a = شماره‌ی فرزندِ راست، b = -1 (فرزندِ چپ همیشه گره‌ی بعدی است)
//   برگ:        a = اولین مثلث،          b = تعدادِ مثلث‌ها
// چیدمانِ مثلث (۳ خانه): [v0 matId] [v1 0] [v2 0]

export const DATA_TEX_WIDTH = 2048;
const LEAF_SIZE = 4;
const BINS = 12;

function makeDataTexture(floats, texelCount) {
  const width = DATA_TEX_WIDTH;
  const height = Math.max(1, Math.ceil(texelCount / width));
  const data = new Float32Array(width * height * 4);
  data.set(floats.subarray(0, Math.min(floats.length, data.length)));
  const tex = new THREE.DataTexture(data, width, height, THREE.RGBAFormat, THREE.FloatType);
  tex.minFilter = THREE.NearestFilter;
  tex.magFilter = THREE.NearestFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

// positions: Float32Array (۹ عدد برای هر مثلث، مختصاتِ جهانی)، matIds: Int32Array (یکی برای هر مثلث)
export function buildBvh(positions, matIds) {
  const triCount = matIds.length;
  const bmin = new Float32Array(triCount * 3);
  const bmax = new Float32Array(triCount * 3);
  const cent = new Float32Array(triCount * 3);
  for (let t = 0; t < triCount; t += 1) {
    for (let k = 0; k < 3; k += 1) {
      const a = positions[t * 9 + k];
      const b = positions[t * 9 + 3 + k];
      const c = positions[t * 9 + 6 + k];
      bmin[t * 3 + k] = Math.min(a, b, c);
      bmax[t * 3 + k] = Math.max(a, b, c);
      cent[t * 3 + k] = (a + b + c) / 3;
    }
  }
  const index = new Int32Array(triCount);
  for (let i = 0; i < triCount; i += 1) index[i] = i;

  const nodes = []; // [minx,miny,minz,a, maxx,maxy,maxz,b]
  const area = (x, y, z) => 2 * (x * y + y * z + z * x);

  // ساختِ بازگشتی با تقسیمِ SAH (bin‌بندی‌شده)؛ اگر برشِ خوبی نبود نصف می‌شود، پس عمق همیشه محدود است
  function build(start, end) {
    const nodeIndex = nodes.length / 8;
    let mnx = Infinity;
    let mny = Infinity;
    let mnz = Infinity;
    let mxx = -Infinity;
    let mxy = -Infinity;
    let mxz = -Infinity;
    let cmnx = Infinity;
    let cmny = Infinity;
    let cmnz = Infinity;
    let cmxx = -Infinity;
    let cmxy = -Infinity;
    let cmxz = -Infinity;
    for (let i = start; i < end; i += 1) {
      const t = index[i];
      mnx = Math.min(mnx, bmin[t * 3]);
      mny = Math.min(mny, bmin[t * 3 + 1]);
      mnz = Math.min(mnz, bmin[t * 3 + 2]);
      mxx = Math.max(mxx, bmax[t * 3]);
      mxy = Math.max(mxy, bmax[t * 3 + 1]);
      mxz = Math.max(mxz, bmax[t * 3 + 2]);
      cmnx = Math.min(cmnx, cent[t * 3]);
      cmny = Math.min(cmny, cent[t * 3 + 1]);
      cmnz = Math.min(cmnz, cent[t * 3 + 2]);
      cmxx = Math.max(cmxx, cent[t * 3]);
      cmxy = Math.max(cmxy, cent[t * 3 + 1]);
      cmxz = Math.max(cmxz, cent[t * 3 + 2]);
    }
    nodes.push(mnx, mny, mnz, 0, mxx, mxy, mxz, 0);
    const count = end - start;
    const makeLeaf = () => {
      nodes[nodeIndex * 8 + 3] = start;
      nodes[nodeIndex * 8 + 7] = count;
    };
    if (count <= LEAF_SIZE) {
      makeLeaf();
      return nodeIndex;
    }

    // بهترین محور و مکانِ برش با SAH
    const cmin = [cmnx, cmny, cmnz];
    const cext = [cmxx - cmnx, cmxy - cmny, cmxz - cmnz];
    let bestCost = Infinity;
    let bestAxis = -1;
    let bestBin = -1;
    for (let axis = 0; axis < 3; axis += 1) {
      if (cext[axis] < 1e-9) continue;
      const cnt = new Int32Array(BINS);
      const bb = new Float32Array(BINS * 6).fill(0);
      for (let i = 0; i < BINS; i += 1) {
        bb[i * 6] = Infinity;
        bb[i * 6 + 1] = Infinity;
        bb[i * 6 + 2] = Infinity;
        bb[i * 6 + 3] = -Infinity;
        bb[i * 6 + 4] = -Infinity;
        bb[i * 6 + 5] = -Infinity;
      }
      const scale = BINS / cext[axis];
      for (let i = start; i < end; i += 1) {
        const t = index[i];
        const bi = Math.min(BINS - 1, Math.floor((cent[t * 3 + axis] - cmin[axis]) * scale));
        cnt[bi] += 1;
        for (let k = 0; k < 3; k += 1) {
          bb[bi * 6 + k] = Math.min(bb[bi * 6 + k], bmin[t * 3 + k]);
          bb[bi * 6 + 3 + k] = Math.max(bb[bi * 6 + 3 + k], bmax[t * 3 + k]);
        }
      }
      // جاروی چپ و راست
      const leftArea = new Float32Array(BINS);
      const leftCount = new Int32Array(BINS);
      let lx0 = Infinity;
      let ly0 = Infinity;
      let lz0 = Infinity;
      let lx1 = -Infinity;
      let ly1 = -Infinity;
      let lz1 = -Infinity;
      let lc = 0;
      for (let i = 0; i < BINS; i += 1) {
        if (cnt[i]) {
          lx0 = Math.min(lx0, bb[i * 6]);
          ly0 = Math.min(ly0, bb[i * 6 + 1]);
          lz0 = Math.min(lz0, bb[i * 6 + 2]);
          lx1 = Math.max(lx1, bb[i * 6 + 3]);
          ly1 = Math.max(ly1, bb[i * 6 + 4]);
          lz1 = Math.max(lz1, bb[i * 6 + 5]);
        }
        lc += cnt[i];
        leftCount[i] = lc;
        leftArea[i] = lc ? area(lx1 - lx0, ly1 - ly0, lz1 - lz0) : 0;
      }
      let rx0 = Infinity;
      let ry0 = Infinity;
      let rz0 = Infinity;
      let rx1 = -Infinity;
      let ry1 = -Infinity;
      let rz1 = -Infinity;
      let rc = 0;
      for (let i = BINS - 1; i > 0; i -= 1) {
        if (cnt[i]) {
          rx0 = Math.min(rx0, bb[i * 6]);
          ry0 = Math.min(ry0, bb[i * 6 + 1]);
          rz0 = Math.min(rz0, bb[i * 6 + 2]);
          rx1 = Math.max(rx1, bb[i * 6 + 3]);
          ry1 = Math.max(ry1, bb[i * 6 + 4]);
          rz1 = Math.max(rz1, bb[i * 6 + 5]);
        }
        rc += cnt[i];
        const lcount = leftCount[i - 1];
        if (!lcount || !rc) continue;
        const cost = leftArea[i - 1] * lcount + area(rx1 - rx0, ry1 - ry0, rz1 - rz0) * rc;
        if (cost < bestCost) {
          bestCost = cost;
          bestAxis = axis;
          bestBin = i;
        }
      }
    }

    let mid;
    if (bestAxis === -1) {
      // همه‌ی مرکزها روی هم — نصف‌کردنِ ساده
      mid = start + (count >> 1);
    } else {
      const scale = BINS / cext[bestAxis];
      let i = start;
      let j = end - 1;
      while (i <= j) {
        const t = index[i];
        const bi = Math.min(BINS - 1, Math.floor((cent[t * 3 + bestAxis] - cmin[bestAxis]) * scale));
        if (bi < bestBin) i += 1;
        else {
          index[i] = index[j];
          index[j] = t;
          j -= 1;
        }
      }
      mid = i;
      if (mid === start || mid === end) mid = start + (count >> 1);
    }
    build(start, mid);
    const right = build(mid, end);
    nodes[nodeIndex * 8 + 3] = right;
    nodes[nodeIndex * 8 + 7] = -1;
    return nodeIndex;
  }
  build(0, triCount);

  const nodeCount = nodes.length / 8;
  const nodeTex = makeDataTexture(Float32Array.from(nodes), nodeCount * 2);
  const triData = new Float32Array(triCount * 12);
  for (let i = 0; i < triCount; i += 1) {
    const t = index[i];
    for (let k = 0; k < 3; k += 1) {
      triData[i * 12 + k * 4] = positions[t * 9 + k * 3];
      triData[i * 12 + k * 4 + 1] = positions[t * 9 + k * 3 + 1];
      triData[i * 12 + k * 4 + 2] = positions[t * 9 + k * 3 + 2];
      triData[i * 12 + k * 4 + 3] = k === 0 ? matIds[t] : 0;
    }
  }
  const triTex = makeDataTexture(triData, triCount * 3);
  return { nodeTex, triTex, nodeCount, triCount };
}

export { makeDataTexture };
